package dev.youndie.site.pages.blog

import androidx.compose.runtime.*
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.*
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.core.layout.NoLayout
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobwebx.frontmatter.*
import com.varabyte.kobwebx.markdown.*

@InitRoute
fun initScreenshotFixturesFromPreviewPage(ctx: InitRouteContext) {
    ctx.data.add(MarkdownContext("blog/ScreenshotFixturesFromPreview.md", FrontMatterElement.Builder { addScalar("title", "Screenshot fixtures declared with @Preview"); addScalar("searchTitle", "Compose Multiplatform screenshot tests from @Preview annotations"); addScalar("date", "2026-08-27"); addScalar("description", "Since 0.3.0 viddik reads androidx.compose.ui.tooling.preview.Preview, so one annotation serves the IDE preview pane, Android's screenshot tooling and the golden-file suite at once."); addScalar("library", "viddik"); }))
}

@Page("/blog/screenshot-fixtures-from-preview")
@Layout(".components.layouts.MarkdownLayout")
@Composable
fun ScreenshotFixturesFromPreviewPage() {
    org.jetbrains.compose.web.dom.P {
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
            org.jetbrains.compose.web.dom.Text("viddik")
        }
        org.jetbrains.compose.web.dom.Text(" renders Compose Multiplatform components through a real Compose Desktop window and diffs them against committed goldens. Until 0.3.0 a fixture stated its name and size in viddik's own vocabulary, on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@ViddikScreenshot")
        }
        org.jetbrains.compose.web.dom.Text(". It now reads them off ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" instead.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("That is worth doing because of where ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" lives. Compose Multiplatform 1.12 ships ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("androidx.compose.ui.tooling.preview.Preview")
        }
        org.jetbrains.compose.web.dom.Text(" in ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("commonMain")
        }
        org.jetbrains.compose.web.dom.Text(", under the same fully-qualified name Android uses. So a single annotation is read by the IDE preview pane, by Android's own ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("com.android.compose.screenshot")
        }
        org.jetbrains.compose.web.dom.Text(" plugin, and by viddik — with nothing in between translating between three vocabularies.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("why-read-preview") }) {
        org.jetbrains.compose.web.dom.Text("Why read ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A component worth a golden usually already has a preview, written so somebody could look at it while building it. Before 0.3.0 that meant two declarations of the same two facts — what the component is called and how big it should be drawn — and a third if an Android module also ran Google's screenshot plugin. Three places to change, and nothing that notices when they disagree.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@ViddikScreenshot")
        }
        org.jetbrains.compose.web.dom.Text(" stays, but only as the opt-in marker. Bare ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" is deliberately not scanned: in an application of any size most previews exist for the IDE and cannot render headless at all — a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("hiltViewModel()")
        }
        org.jetbrains.compose.web.dom.Text(" here, a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("LocalContext")
        }
        org.jetbrains.compose.web.dom.Text(" read there — so capturing every one of them produces a suite that fails for reasons that have nothing to do with the UI. Android's own tool reached the same conclusion and requires ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewTest")
        }
        org.jetbrains.compose.web.dom.Text(" alongside ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("what-is-read") }) {
        org.jetbrains.compose.web.dom.Text("What is read")
    }
    org.jetbrains.compose.web.dom.Table {
        org.jetbrains.compose.web.dom.Thead {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("@Preview")
                    }
                    org.jetbrains.compose.web.dom.Text(" field")
                }
                org.jetbrains.compose.web.dom.Th {
                    org.jetbrains.compose.web.dom.Text("effect")
                }
            }
        }
        org.jetbrains.compose.web.dom.Tbody {
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("name")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("group")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("golden file name and the group in the component browser")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("widthDp")
                    }
                    org.jetbrains.compose.web.dom.Text(", ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("heightDp")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("capture size in pixels — viddik renders at density 1")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("uiMode")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the night bits make this fixture render dark")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("fontScale")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("scales text inside the capture, not the canvas")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("device")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("the size out of a ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("spec:")
                    }
                    org.jetbrains.compose.web.dom.Text(" string, and nothing else")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("repeated ")
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("@Preview")
                    }
                    org.jetbrains.compose.web.dom.Text(", multipreview")
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("one fixture per preview, resolved recursively")
                }
            }
            org.jetbrains.compose.web.dom.Tr {
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Code {
                        org.jetbrains.compose.web.dom.Text("@PreviewWrapper")
                    }
                }
                org.jetbrains.compose.web.dom.Td {
                    org.jetbrains.compose.web.dom.Text("wraps the fixture before it is captured")
                }
            }
        }
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Precedence per field is the argument on ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@ViddikScreenshot")
        }
        org.jetbrains.compose.web.dom.Text(", then the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" field, then viddik's default — so a fixture that spells everything the old way resolves exactly as it did.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("uiMode")
        }
        org.jetbrains.compose.web.dom.Text(" and the older ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("darkVariant")
        }
        org.jetbrains.compose.web.dom.Text(" answer different questions: ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("uiMode")
        }
        org.jetbrains.compose.web.dom.Text(" says ")
        org.jetbrains.compose.web.dom.Em {
            org.jetbrains.compose.web.dom.Text("this")
        }
        org.jetbrains.compose.web.dom.Text(" fixture is dark, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("darkVariant")
        }
        org.jetbrains.compose.web.dom.Text(" asks for a second, dark entry beside a light one. Setting both is a compile error. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("uiMode")
        }
        org.jetbrains.compose.web.dom.Text(" is a bit field rather than a value — ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewLightDark")
        }
        org.jetbrains.compose.web.dom.Text(" carries 33 on its dark half, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("UI_MODE_NIGHT_YES")
        }
        org.jetbrains.compose.web.dom.Text(" combined with ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("UI_MODE_TYPE_NORMAL")
        }
        org.jetbrains.compose.web.dom.Text(" — so it is masked, not compared.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("installation") }) {
        org.jetbrains.compose.web.dom.Text("Installation")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""// settings.gradle.kts
pluginManagement { repositories { maven("https://reposilite.kotlin.website/snapshots") } }

// build.gradle.kts of the module holding the fixtures
plugins {
    id("com.google.devtools.ksp") version "<your Kotlin compiler version>"
    id("ru.workinprogress.viddik") version "0.3.0.15"
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("The plugin adds the artefacts, puts the processor on the right ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ksp*")
        }
        org.jetbrains.compose.web.dom.Text(" configuration for your module's shape, and registers ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikRecord")
        }
        org.jetbrains.compose.web.dom.Text(" / ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikVerify")
        }
        org.jetbrains.compose.web.dom.Text(" / ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddikShowroom")
        }
        org.jetbrains.compose.web.dom.Text(".")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("0.3.x requires Compose Multiplatform 1.12.x. viddik reaches past the public test API into ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("ComposeScene")
        }
        org.jetbrains.compose.web.dom.Text(" and Skiko, so it is bound to one Compose line rather than a range, and a mismatch surfaces at runtime on the first captured frame rather than at compile time.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("how-it-looks") }) {
        org.jetbrains.compose.web.dom.Text("How it looks")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""@ViddikScreenshot
@PreviewWrapper(AppPreviewTheme::class)
@PreviewLightDark
@Composable
fun PrimaryButton() {
    Button(onClick = {}) { Text("Continue") }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Two goldens, ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PrimaryButton - Light")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PrimaryButton - Dark")
        }
        org.jetbrains.compose.web.dom.Text(", both drawn inside ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("AppPreviewTheme")
        }
        org.jetbrains.compose.web.dom.Text(". The same declaration is what the IDE shows in its preview pane.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("the-theme-a-fixture-used-to-have-to-remember") }) {
        org.jetbrains.compose.web.dom.Text("The theme a fixture used to have to remember")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewWrapper")
        }
        org.jetbrains.compose.web.dom.Text(" is the part that changes how a suite is written, and the reason is mechanical.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("A theme cannot be forced onto a composable from outside its composition. The capture engine renders whatever ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("content()")
        }
        org.jetbrains.compose.web.dom.Text(" it is handed, and if that content calls its own ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("MaterialTheme")
        }
        org.jetbrains.compose.web.dom.Text(", that call wins over anything provided further out. Goldens are only portable across machines when text is drawn in a bundled font rather than the host's, so every fixture had to remember to call the harness that supplies one — and a fixture that forgot did not fail. It recorded a golden drawn in whatever font the recording machine happened to have, which then failed on somebody else's.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewWrapper")
        }
        org.jetbrains.compose.web.dom.Text(" names a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("PreviewWrapperProvider")
        }
        org.jetbrains.compose.web.dom.Text(", and viddik applies it during code generation — outside the composition, where a theme can still be imposed:")
    }
    org.jetbrains.compose.web.dom.Pre { org.jetbrains.compose.web.dom.Code(attrs = { classes("language-kotlin") }) { org.jetbrains.compose.web.dom.Text("""class AppPreviewTheme : PreviewWrapperProvider {
    @Composable
    override fun Wrap(content: @Composable () -> Unit) {
        MaterialTheme(typography = viddikTypography(), content = content)
    }
}
""") } }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Because the annotation may also sit on an annotation class, a project's own ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@AppPreviews")
        }
        org.jetbrains.compose.web.dom.Text(" can carry the theme and the light/dark pair together, and fixtures declare neither.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("viddik's own suite is the check on this: it contains a fixture with no theme call of its own, and that fixture verifies against one committed golden on Linux, macOS and Windows in CI. It could only do that if the wrapper really did supply the bundled font — the three platforms use three different font backends, and a host font would diverge immediately.")
    }
    org.jetbrains.compose.web.dom.H2(attrs = { id("limitations") }) {
        org.jetbrains.compose.web.dom.Text("Limitations")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("device")
        }
        org.jetbrains.compose.web.dom.Text(" is read for its size only, and only in the ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("spec:width=…dp,height=…dp")
        }
        org.jetbrains.compose.web.dom.Text(" form. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("dpi")
        }
        org.jetbrains.compose.web.dom.Text(", ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("orientation")
        }
        org.jetbrains.compose.web.dom.Text(" and ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("isRound")
        }
        org.jetbrains.compose.web.dom.Text(" are a density or a device shape that a flat canvas has no equivalent for, and a named device such as ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("id:pixel_5")
        }
        org.jetbrains.compose.web.dom.Text(" keeps its dimensions in Android's catalogue rather than in the annotation. Both are warnings rather than errors: a fixture carrying ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("device")
        }
        org.jetbrains.compose.web.dom.Text(" for the IDE's sake is still a good fixture, it just does not get that device.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("fontScale")
        }
        org.jetbrains.compose.web.dom.Text(" scales text and not the canvas, because the capture path treats a dp and a pixel as the same unit; scaling the density would resize every golden a ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewFontScale")
        }
        org.jetbrains.compose.web.dom.Text(" produces. ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("darkVariant")
        }
        org.jetbrains.compose.web.dom.Text(" alongside several previews is refused outright — it doubles every fixture a function makes, so one ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@PreviewFontScale")
        }
        org.jetbrains.compose.web.dom.Text(" would quietly become fourteen goldens.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Only the common ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("androidx.compose.ui.tooling.preview.Preview")
        }
        org.jetbrains.compose.web.dom.Text(" is read; the older desktop-only ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("androidx.compose.desktop.ui.tooling.preview.Preview")
        }
        org.jetbrains.compose.web.dom.Text(" is not, because it cannot serve Android. An Android consumer of ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("viddik-annotations")
        }
        org.jetbrains.compose.web.dom.Text(" needs ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("compileSdk = 37")
        }
        org.jetbrains.compose.web.dom.Text(", which is what Compose Multiplatform 1.12 requires of everything depending on it.")
    }
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Finally, sharing an annotation is not sharing a renderer. Point Google's screenshot plugin at the same ")
        org.jetbrains.compose.web.dom.Code {
            org.jetbrains.compose.web.dom.Text("@Preview")
        }
        org.jetbrains.compose.web.dom.Text(" functions and it renders them through LayoutLib while viddik renders them through Skiko: two sets of goldens that will never agree pixel for pixel, and are not meant to be compared.")
    }
    com.varabyte.kobweb.silk.components.layout.HorizontalDivider()
    org.jetbrains.compose.web.dom.P {
        org.jetbrains.compose.web.dom.Text("Source: ")
        com.varabyte.kobweb.silk.components.navigation.Link("https://github.com/youndie/viddik") {
            org.jetbrains.compose.web.dom.Text("github.com/youndie/viddik")
        }
    }
}
